import { create } from 'zustand';
import type { LayerBulk, ParamBulkSpec } from '../core/params/bulk';
import { TEXT_LAYER_PARAMS } from '../core/params/textLayer';
import { buildTextSchema } from '../core/text/textParams';
import {
  coerceValue,
  defaultValues,
  findParam,
  withDefaults,
  type ParamSchema,
  type ParamValue,
  type ParamValues,
} from '../core/params/types';
import { randomizeParam, randomizeValues, type RandomizeStrength } from '../core/params/randomize';
import { createRng, randomSeed } from '../core/rng';
import { BACKGROUND_PARAMS } from '../core/background';
import { defaultPostChain, POST_EFFECT_BY_ID } from '../core/postfx/chain';
import type { PostChainConfig } from '../core/postfx/types';
import { DEFAULT_FONT_ID } from '../core/fonts/catalog';
import type { LoadedFont } from '../core/fonts/fontLoader';
import type { AnchorX, AnchorY, TextSettings } from '../core/text/settings';
import { toTextSettings } from '../core/text/textParams';
import { getTechnique } from '../techniques/registry';
import { createLayer, duplicateLayer, TEXT_TECHNIQUE_ID, type Layer } from './layer';
import { emptyHistory, pushHistory, redo, undo, type History } from './history';

export interface CanvasSizePreset {
  id: string;
  label: string;
  width: number;
  height: number;
  note?: string;
}

export const CANVAS_PRESETS: CanvasSizePreset[] = [
  { id: 'square-1080', label: 'Quadrat 1080', width: 1080, height: 1080 },
  { id: 'portrait-1080', label: 'Portrait 4:5', width: 1080, height: 1350 },
  { id: 'story', label: 'Story 9:16', width: 1080, height: 1920 },
  { id: 'hd', label: 'HD 16:9', width: 1920, height: 1080 },
  { id: 'square-2000', label: 'Quadrat 2000', width: 2000, height: 2000 },
  { id: 'a4', label: 'A4 @300dpi', width: 2480, height: 3508, note: 'Print' },
  { id: 'a3', label: 'A3 @300dpi', width: 3508, height: 4961, note: 'Print' },
];

export interface CanvasSettings {
  width: number;
  height: number;
  exportScale: number;
}

export type FieldView = 'render' | 'alpha' | 'sdf' | 'gradient' | 'outline' | 'samples' | 'glyphs';

/**
 * The undoable document.
 *
 * Everything that defines the artwork lives here and nowhere else, which is what makes
 * snapshot undo, presets, share links and batch variants all read from one shape.
 * Transient UI state (loaded font object, panel widths, the field inspector) is kept
 * outside so that toggling a debug view is not an undo step.
 */
export interface StudioDocument {
  /** Flat parameter values for the text geometry schema. */
  textValues: ParamValues;
  textBulk: LayerBulk;
  fontId: string;
  anchorX: AnchorX;
  anchorY: AnchorY;
  canvas: CanvasSettings;
  /** Values of the background schema. */
  background: ParamValues;
  /** Post-processing chain, keyed by effect id. */
  postFx: PostChainConfig;
  seed: number;
  seedLocked: boolean;
  /** Draw order: index 0 is the bottom of the stack. */
  layers: Layer[];
  activeLayerId: string;
}

function createDocument(): StudioDocument {
  const textLayer = createLayer(TEXT_TECHNIQUE_ID, 'Text', TEXT_LAYER_PARAMS);
  return {
    textValues: defaultValues(buildTextSchema(null)),
    textBulk: {},
    fontId: DEFAULT_FONT_ID,
    anchorX: 'center',
    anchorY: 'middle',
    canvas: { width: 1080, height: 1350, exportScale: 2 },
    background: defaultValues(BACKGROUND_PARAMS),
    postFx: defaultPostChain(),
    seed: randomSeed(),
    seedLocked: false,
    layers: [textLayer],
    activeLayerId: textLayer.id,
  };
}

export interface StudioState {
  doc: StudioDocument;
  history: History<StudioDocument>;

  // Transient state — not undoable, not part of a preset.
  font: LoadedFont | null;
  fontLoading: boolean;
  fontError: string | null;
  fieldView: FieldView;
  sampleCount: number;
  paramSearch: string;
  /** Last action label, shown briefly in the topbar. */
  lastAction: string | null;

  // ── Text ────────────────────────────────────────────────────────────────
  setTextValue: (key: string, value: ParamValue) => void;
  setFontId: (id: string) => void;
  setAnchor: (x: AnchorX, y: AnchorY) => void;
  setTextBulk: (key: string, spec: ParamBulkSpec | undefined) => void;
  toggleTextLock: (key: string) => void;
  randomizeTextParam: (key: string) => void;

  // ── Canvas, background, post-processing & seed ──────────────────────────
  setCanvas: (patch: Partial<CanvasSettings>) => void;
  setBackgroundValue: (key: string, value: ParamValue) => void;
  setPostEnabled: (effectId: string, enabled: boolean) => void;
  setPostValue: (effectId: string, key: string, value: ParamValue) => void;
  resetPostEffect: (effectId: string) => void;
  setSeed: (seed: number) => void;
  rollSeed: () => void;
  toggleSeedLock: () => void;

  // ── Layers ──────────────────────────────────────────────────────────────
  addLayer: (techniqueId: string) => void;
  removeLayer: (id: string) => void;
  duplicateLayerById: (id: string) => void;
  moveLayer: (id: string, toIndex: number) => void;
  selectLayer: (id: string) => void;
  updateLayer: (id: string, patch: Partial<Omit<Layer, 'id' | 'values' | 'bulk'>>) => void;
  setLayerValue: (id: string, key: string, value: ParamValue) => void;
  setLayerBulk: (id: string, key: string, spec: ParamBulkSpec | undefined) => void;
  toggleLayerLock: (id: string, key: string) => void;
  randomizeLayerParam: (id: string, key: string) => void;
  resetLayerParams: (id: string) => void;

  // ── Global actions ──────────────────────────────────────────────────────
  randomizeAll: (strength: RandomizeStrength) => void;
  undoAction: () => void;
  redoAction: () => void;
  replaceDocument: (doc: StudioDocument, label: string) => void;

  // ── Transient setters ───────────────────────────────────────────────────
  setFont: (font: LoadedFont | null) => void;
  setFontLoading: (loading: boolean) => void;
  setFontError: (error: string | null) => void;
  setFieldView: (view: FieldView) => void;
  setSampleCount: (n: number) => void;
  setParamSearch: (query: string) => void;
}

/** Schema for a layer, resolved through the technique registry. */
export function layerSchema(layer: Layer): ParamSchema {
  if (layer.techniqueId === TEXT_TECHNIQUE_ID) return TEXT_LAYER_PARAMS;
  return getTechnique(layer.techniqueId)?.params ?? [];
}

export const useStudio = create<StudioState>((set, get) => {
  /**
   * Applies a change to the document and records the previous state for undo.
   * `coalesceKey` folds a burst of updates — a slider drag — into one step.
   */
  const commit = (
    label: string,
    mutate: (doc: StudioDocument) => StudioDocument,
    coalesceKey?: string,
  ): void => {
    const { doc, history } = get();
    const next = mutate(doc);
    if (next === doc) return;
    set({
      doc: next,
      history: pushHistory(history, doc, label, coalesceKey),
      lastAction: label,
    });
  };

  const mapLayer = (doc: StudioDocument, id: string, fn: (layer: Layer) => Layer): StudioDocument => {
    const index = doc.layers.findIndex((l) => l.id === id);
    if (index < 0) return doc;
    const layers = doc.layers.slice();
    layers[index] = fn(layers[index]);
    return { ...doc, layers };
  };

  return {
    doc: createDocument(),
    history: emptyHistory<StudioDocument>(),

    font: null,
    fontLoading: false,
    fontError: null,
    fieldView: 'render',
    sampleCount: 900,
    paramSearch: '',
    lastAction: null,

    // ── Text ──────────────────────────────────────────────────────────────
    setTextValue: (key, value) =>
      commit(
        `Text: ${key}`,
        (doc) => ({ ...doc, textValues: { ...doc.textValues, [key]: value } }),
        `text:${key}`,
      ),

    setFontId: (id) =>
      commit('Schrift gewechselt', (doc) => {
        // Axis values belong to the previous font; drop them so the new font starts
        // from its own defaults rather than inheriting incompatible coordinates.
        const textValues: ParamValues = {};
        for (const [k, v] of Object.entries(doc.textValues)) {
          if (!k.startsWith('axis:')) textValues[k] = v;
        }
        return { ...doc, fontId: id, textValues };
      }),

    setAnchor: (anchorX, anchorY) => commit('Anker', (doc) => ({ ...doc, anchorX, anchorY })),

    setTextBulk: (key, spec) =>
      commit(`Bereich: ${key}`, (doc) => {
        const textBulk = { ...doc.textBulk };
        if (spec) textBulk[key] = spec;
        else delete textBulk[key];
        return { ...doc, textBulk };
      }),

    toggleTextLock: (key) =>
      commit('Sperre', (doc) => {
        const current = doc.textBulk[key];
        return {
          ...doc,
          textBulk: { ...doc.textBulk, [key]: { ...current, locked: !current?.locked } },
        };
      }),

    randomizeTextParam: (key) => {
      const { font, doc } = get();
      const param = findParam(buildTextSchema(font), key);
      if (!param) return;
      const rng = createRng(randomSeed());
      const value = randomizeParam(param, doc.textValues[key], doc.textBulk[key], rng, 'wild');
      commit(`Würfeln: ${param.label}`, (d) => ({ ...d, textValues: { ...d.textValues, [key]: value } }));
    },

    // ── Canvas & seed ─────────────────────────────────────────────────────
    setCanvas: (patch) =>
      commit('Canvas', (doc) => ({ ...doc, canvas: { ...doc.canvas, ...patch } }), 'canvas'),

    setBackgroundValue: (key, value) =>
      commit(
        'Hintergrund',
        (doc) => ({ ...doc, background: { ...doc.background, [key]: value } }),
        `bg:${key}`,
      ),

    setPostEnabled: (effectId, enabled) =>
      commit(`Post-FX: ${effectId}`, (doc) => ({
        ...doc,
        postFx: { ...doc.postFx, [effectId]: { ...doc.postFx[effectId], enabled } },
      })),

    setPostValue: (effectId, key, value) =>
      commit(
        'Post-FX',
        (doc) => {
          const entry = doc.postFx[effectId];
          if (!entry) return doc;
          return {
            ...doc,
            postFx: { ...doc.postFx, [effectId]: { ...entry, values: { ...entry.values, [key]: value } } },
          };
        },
        `postfx:${effectId}:${key}`,
      ),

    resetPostEffect: (effectId) =>
      commit('Post-FX zurückgesetzt', (doc) => {
        const effect = POST_EFFECT_BY_ID.get(effectId);
        if (!effect) return doc;
        return {
          ...doc,
          postFx: {
            ...doc.postFx,
            [effectId]: { ...doc.postFx[effectId], values: defaultValues(effect.params) },
          },
        };
      }),

    setSeed: (seed) => commit('Seed', (doc) => ({ ...doc, seed }), 'seed'),

    rollSeed: () =>
      commit('Seed gewürfelt', (doc) => (doc.seedLocked ? doc : { ...doc, seed: randomSeed() })),

    toggleSeedLock: () => commit('Seed-Sperre', (doc) => ({ ...doc, seedLocked: !doc.seedLocked })),

    // ── Layers ────────────────────────────────────────────────────────────
    addLayer: (techniqueId) => {
      const technique = getTechnique(techniqueId);
      if (!technique) return;
      commit(`Layer: ${technique.name}`, (doc) => {
        const layer = createLayer(techniqueId, technique.name, technique.params);
        return { ...doc, layers: [...doc.layers, layer], activeLayerId: layer.id };
      });
    },

    removeLayer: (id) =>
      commit('Layer gelöscht', (doc) => {
        const layer = doc.layers.find((l) => l.id === id);
        // The text layer is the field itself; it can be hidden but not removed.
        if (!layer || layer.techniqueId === TEXT_TECHNIQUE_ID) return doc;
        const layers = doc.layers.filter((l) => l.id !== id);
        const activeLayerId = doc.activeLayerId === id ? layers[layers.length - 1].id : doc.activeLayerId;
        return { ...doc, layers, activeLayerId };
      }),

    duplicateLayerById: (id) =>
      commit('Layer dupliziert', (doc) => {
        const index = doc.layers.findIndex((l) => l.id === id);
        if (index < 0 || doc.layers[index].techniqueId === TEXT_TECHNIQUE_ID) return doc;
        const copy = duplicateLayer(doc.layers[index]);
        const layers = doc.layers.slice();
        layers.splice(index + 1, 0, copy);
        return { ...doc, layers, activeLayerId: copy.id };
      }),

    moveLayer: (id, toIndex) =>
      commit('Reihenfolge', (doc) => {
        const from = doc.layers.findIndex((l) => l.id === id);
        const to = Math.max(0, Math.min(doc.layers.length - 1, toIndex));
        if (from < 0 || from === to) return doc;
        const layers = doc.layers.slice();
        const [moved] = layers.splice(from, 1);
        layers.splice(to, 0, moved);
        return { ...doc, layers };
      }),

    selectLayer: (id) => set((s) => ({ doc: { ...s.doc, activeLayerId: id } })),

    updateLayer: (id, patch) =>
      commit('Layer', (doc) => mapLayer(doc, id, (l) => ({ ...l, ...patch })), `layer:${id}`),

    setLayerValue: (id, key, value) =>
      commit(
        'Parameter',
        (doc) => mapLayer(doc, id, (l) => ({ ...l, values: { ...l.values, [key]: value } })),
        `layer:${id}:${key}`,
      ),

    setLayerBulk: (id, key, spec) =>
      commit('Bereich', (doc) =>
        mapLayer(doc, id, (l) => {
          const bulk = { ...l.bulk };
          if (spec) bulk[key] = spec;
          else delete bulk[key];
          return { ...l, bulk };
        }),
      ),

    toggleLayerLock: (id, key) =>
      commit('Sperre', (doc) =>
        mapLayer(doc, id, (l) => {
          const current = l.bulk[key];
          return { ...l, bulk: { ...l.bulk, [key]: { ...current, locked: !current?.locked } } };
        }),
      ),

    randomizeLayerParam: (id, key) => {
      const layer = get().doc.layers.find((l) => l.id === id);
      if (!layer) return;
      const param = findParam(layerSchema(layer), key);
      if (!param) return;
      const rng = createRng(randomSeed());
      const value = randomizeParam(param, layer.values[key], layer.bulk[key], rng, 'wild');
      commit(`Würfeln: ${param.label}`, (doc) =>
        mapLayer(doc, id, (l) => ({ ...l, values: { ...l.values, [key]: value } })),
      );
    },

    resetLayerParams: (id) =>
      commit('Parameter zurückgesetzt', (doc) =>
        mapLayer(doc, id, (l) => ({ ...l, values: defaultValues(layerSchema(l)) })),
      ),

    // ── Global ────────────────────────────────────────────────────────────
    randomizeAll: (strength) => {
      const { font } = get();
      const textSchema = buildTextSchema(font);
      commit(`Randomize (${strength})`, (doc) => {
        const rng = createRng(randomSeed());
        return {
          ...doc,
          seed: doc.seedLocked ? doc.seed : randomSeed(),
          textValues: randomizeValues(textSchema, doc.textValues, doc.textBulk, rng, strength),
          layers: doc.layers.map((layer) => ({
            ...layer,
            seed: layer.seedLocked ? layer.seed : randomSeed(),
            values: randomizeValues(layerSchema(layer), layer.values, layer.bulk, rng, strength),
          })),
        };
      });
    },

    undoAction: () => {
      const { history, doc } = get();
      const result = undo(history, doc);
      if (!result) return;
      set({ doc: result.doc, history: result.history, lastAction: `Rückgängig: ${result.label}` });
    },

    redoAction: () => {
      const { history, doc } = get();
      const result = redo(history, doc);
      if (!result) return;
      set({ doc: result.doc, history: result.history, lastAction: `Wiederholt: ${result.label}` });
    },

    replaceDocument: (next, label) => commit(label, () => next),

    // ── Transient ─────────────────────────────────────────────────────────
    setFont: (font) => {
      set({ font });
      // A newly loaded font brings its own axes; fill in any values the document does
      // not have yet so the sliders start at the font's defaults rather than empty.
      const { doc } = get();
      const filled = withDefaults(buildTextSchema(font), doc.textValues);
      set({ doc: { ...doc, textValues: filled } });
    },
    setFontLoading: (fontLoading) => set({ fontLoading }),
    setFontError: (fontError) => set({ fontError }),
    setFieldView: (fieldView) => set({ fieldView }),
    setSampleCount: (sampleCount) => set({ sampleCount }),
    setParamSearch: (paramSearch) => set({ paramSearch }),
  };
});

// ── Selectors ────────────────────────────────────────────────────────────────

export function selectActiveLayer(state: StudioState): Layer {
  return state.doc.layers.find((l) => l.id === state.doc.activeLayerId) ?? state.doc.layers[0];
}

export function selectTextLayer(state: StudioState): Layer | undefined {
  return state.doc.layers.find((l) => l.techniqueId === TEXT_TECHNIQUE_ID);
}

/** Builds the typed settings the layout engine needs from the flat document values. */
export function selectTextSettings(state: StudioState): TextSettings {
  const textLayer = selectTextLayer(state);
  const values = textLayer?.values ?? {};
  const renderMode = values.renderMode;
  const strokeWidth = values.strokeWidth;
  return toTextSettings(state.doc.textValues, {
    fontId: state.doc.fontId,
    anchorX: state.doc.anchorX,
    anchorY: state.doc.anchorY,
    visible: textLayer?.visible ?? true,
    renderMode: typeof renderMode === 'string' ? (renderMode as TextSettings['renderMode']) : 'fill',
    strokeWidth: typeof strokeWidth === 'number' ? strokeWidth : 3,
  });
}

/** Normalises loaded values against a schema — used by presets and share links. */
export function sanitiseValues(schema: ParamSchema, values: ParamValues): ParamValues {
  const out: ParamValues = {};
  for (const p of schema) out[p.key] = coerceValue(p, values[p.key]);
  return out;
}
